/**
 * Feature permissions — the server's per-user feature switches (FeaturesData).
 *
 * A second permission system beside roles (permissions.js), and the one legacy
 * gates almost every Manage Portfolio control on: its `v-permission` directive
 * (utils/permissionDirective.js + utils/permissions.js) hides an element unless
 * `"feature.endpoint"` is switched on for this user. The data arrives with the
 * sign-in (getUserToken `FeaturesData`), is kept in the auth slice — only the
 * CURRENT product's branch (features/auth/roles.js) — and is restored from the
 * `featuresData` cookie on reload (security/auth/sessionMirror.js).
 *
 * Branch shape: { role_name, features: [{ name, enabled, endpoints: [{ name, enabled }] }] }
 *
 * One rule, legacy's v-permission rule: a `"feature.endpoint"` key needs the
 * feature AND the endpoint enabled; a plain `"feature"` key needs the feature.
 * Anything missing — no data, no feature, no endpoint — is NOT allowed. Two
 * legacy faults are not copied:
 *  - its hand-written checks (ManageAccount.vue, FooterActions.vue,
 *    ActionButtons.vue, InstancesListTable.vue) skipped `feature.enabled`, so a
 *    switched-off feature still showed controls whose endpoints were on;
 *  - the same checks read `.endpoints` / `.enabled` with no `?.`, so a user
 *    whose data lacked one entry crashed the page instead of losing a button.
 *
 * Use the KEYS below, never a typed string, so every gate is greppable.
 */

/** The switches legacy checks: Manage Portfolio's (feature `manage_portfolio`), and Explorer. */
export const FEATURES = Object.freeze({
  /** Upload a file + download templates (legacy UploadDownloadTemplate.vue). */
  FILE_UPLOAD: 'manage_portfolio.file-upload/validate',
  /** Add an instance by hand, and edit table cells (/input/validate). */
  INPUT_VALIDATE: 'manage_portfolio.input/validate',
  /** Find & Replace and the data-correction help (/input/correct). */
  INPUT_CORRECT: 'manage_portfolio.input/correct',
  SAVE_PORTFOLIO: 'manage_portfolio.save-portfolio',
  UPDATE_PORTFOLIO: 'manage_portfolio.update-portfolio',
  DELETE_PORTFOLIO: 'manage_portfolio.delete-portfolio',
  COST_ADVICE: 'manage_portfolio.cost-advice',
  /** The "List of <provider> Regions →" link (/instances/summary). */
  REGIONS_LIST: 'manage_portfolio.instances/summary',
  /**
   * CCA's "Cloud Usage Reports" link in Portfolio details (ManageAccount.vue:51)
   * — and the page it opens (legacy left the page itself ungated).
   */
  CLOUD_ACCOUNTS: 'manage_portfolio.cloud-accounts',
  /** That page's Test button (AddCloudUsageReports.vue:198). */
  CLOUD_ACCOUNTS_TEST: 'manage_portfolio.cloud-accounts/test_account',
  /**
   * The Explorer page (CCA). A plain feature key, as legacy checks it:
   * hasFeaturePermission('explorer')?.enabled behind the desktop nav item
   * (Headerbar.vue:403/:411). Legacy left the route and its mobile menu entry
   * ungated; here the page itself checks it.
   */
  EXPLORER: 'explorer',
});

/**
 * The per-connector telemetry switches (legacy utils/telemetryConfig.js
 * TELEMETRY_PERMISSIONS / SAVE_TELEMETRY_PERMISSIONS :219-233): one key per
 * connector slug, so built here rather than typed at the call site.
 */
export const TELEMETRY_FEATURES = Object.freeze({
  /** Test Connection for this connector. */
  connection: (slug) => `manage_portfolio.telemetry/connection/${slug}`,
  /** Save & Create for this connector. */
  metrics: (slug) => `manage_portfolio.telemetry/metrics/${slug}`,
});

/**
 * Is `key` switched on in this feature branch?
 *
 * @param {{ features?: object[] } | null} branch  the auth slice's `features`
 * @param {string} key  a FEATURES value ("feature.endpoint") or a feature name
 */
export function isFeatureEnabled(branch, key) {
  if (typeof key !== 'string' || !key) return false;
  const features = Array.isArray(branch?.features) ? branch.features : [];
  // Split at the FIRST dot only: endpoint names contain '/', never '.'.
  const dot = key.indexOf('.');
  const featureName = dot === -1 ? key : key.slice(0, dot);
  const feature = features.find((f) => f?.name === featureName);
  if (feature?.enabled !== true) return false;
  if (dot === -1) return true;
  const endpointName = key.slice(dot + 1);
  const endpoints = Array.isArray(feature.endpoints) ? feature.endpoints : [];
  return endpoints.find((e) => e?.name === endpointName)?.enabled === true;
}
